import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { RootState } from "typedefs";

import CoachInfo from "./CoachInfo";
import DaysPanel from "./DaysPanel";
import DaysPanelForAllCoaches from "./DayPanelForAllCoaches";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { LessonSchedule } from "app/models/EventModel";

import styles from "./DateTime.module.scss";
import { useTranslation } from "react-i18next";
import {
  customLessonSlotParams,
  getSingleCoachPublicTimeSlots,
  mapDays,
} from "../helpers";

type SlotParams = {
  coach_profile_ids: string[];
  from: string;
  to?: string;
  slot_duration: number;
  lesson_rate_id?: number;
  ignore_lesson_id?: number | null;
  // The custom lesson that the athlete moves. See customLessonSlotParams.
  lesson_schedule_id?: number;
};

const timeSlotsForCoaches = (params: SlotParams) => {
  const coachId = params.coach_profile_ids[0];

  return schedulingFetchGet(
    `/coach_profiles/${coachId}/available_times`,
    params,
  )
    .then((rawSlots) => {
      const parsedSlots = {};

      rawSlots.forEach((coachSlots: any) => {
        const coachId = coachSlots.coachProfileId;
        const slotModels =
          coachSlots.slots?.map((slot) => {
            return new AvailableSlotModel(slot);
          }) || [];

        parsedSlots[coachId] = slotModels;
      });

      return parsedSlots;
    })
    .catch((e) => console.error(e));
};

const DateAndTime = ({
  viewDate,
  onViewDateChange,
  coaches,
  duration,
  isPublicPage = false,
  hasPricings,
  pricingValue,
  submitForm,
}) => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  const lesson = useSelector(
    (state: RootState): LessonSchedule => state.bookSession.selectedSession,
  );

  const dayStr = viewDate.toISODate();
  const minutesDuration = duration?.durationInMinutes;

  const singleCoach = coaches.length === 1;

  // Check if booking limit is reached (remaining_bookings === 0 means limit reached, null means unlimited)
  // Note: API returns snake_case, so we check both formats for compatibility
  const remainingBookings = pricingValue?.remainingBookings ?? pricingValue?.remaining_bookings;
  const isBookingLimitReached = remainingBookings === 0;

  const coachIdsStr = coaches
    .filter((c) => !c.skipSlots)
    .map((c) => c.id)
    .join("-");

  const coachPublicIdsStr = coaches
    .filter((c) => !c.skipSlots)
    .map((c) => c.calendarUuid)
    .join("-");
  const timeZone = coaches[0].timeZone;
  const today = DateTime.now().setZone(timeZone).startOf("day");
  const [slotsGroupedByDate, setSlots] = useState<
    Record<string, AvailableSlotModel[]>
  >({});
  // CN-2652 #30: the screen should open on the next day with availability, not
  // on today with "No available timeslots". Once only — after that the day is
  // the athlete's to choose, including an empty one.
  const hasDefaultedDay = useRef(false);

  useEffect(() => {
    // Skip fetching timeslots if booking limit is reached
    if (isBookingLimitReached) {
      setSlots({});
      setLoading(false);
      return;
    }

    if (!minutesDuration) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let params: SlotParams = {
      slot_duration: minutesDuration,
      from: dayStr,
      coach_profile_ids: !isPublicPage
        ? coachIdsStr.split("-")
        : coachPublicIdsStr.split("-"),

      ignore_lesson_id: lesson?.id,
      lesson_rate_id: pricingValue?.id,
      ...customLessonSlotParams(pricingValue, lesson),
    };

    if (!isPublicPage && !singleCoach) {
      timeSlotsForCoaches(params).then((slots: any[]) => {
        setTimeSlots(slots);
        setLoading(false);
      });
    }

    let from: DateTime;

    if (viewDate.diff(today).as("days") <= 7) {
      from = today;
    } else {
      from = viewDate.minus({ days: 7 }).startOf("day");
    }

    const to = viewDate.plus({ days: 7 }).endOf("day");

    const initialAccumulator = mapDays(from, to);

    params = {
      ...params,
      from: from.toISO(),
      to: to.toISO(),
    };

    getSingleCoachPublicTimeSlots(params, initialAccumulator, isPublicPage)
      .then((slots: Record<string, AvailableSlotModel[]>) => {
        setSlots(slots);
        setLoading(false);

        if (hasDefaultedDay.current) {
          return;
        }

        const currentDay = viewDate.toISODate();
        if ((slots[currentDay] || []).length > 0) {
          hasDefaultedDay.current = true;
          return;
        }

        const nextDay = Object.keys(slots)
          .filter((day) => day >= currentDay && (slots[day] || []).length > 0)
          .sort()[0];

        if (nextDay) {
          hasDefaultedDay.current = true;
          // Through the parent's handler, not straight to the store: the view
          // date lives in this screen's own state, seeded from the store once, so
          // dispatching alone changed nothing on screen.
          onViewDateChange(DateTime.fromISO(nextDay));
        }
      });
  }, [viewDate, pricingValue?.id, duration?.durationInMinutes, lesson?.id, isPublicPage, isBookingLimitReached]);

  // CN-2652 #29: no paging as you scroll. The day changes through the calendar,
  // and every change refetches the window around it, so the loadMore/loadPrevious
  // pair that used to extend the range on scroll — and the scroll listener and
  // scroll-into-view that went with them — have nothing left to do.

  const renderCoaches = () => {
    return (
      <div className={styles.coach_list}>
        {coaches.map((coach, index) => (
          <div
            key={coach.id ? coach.id : coach.calendarUuid}
            className={singleCoach ? styles.single_list : styles.multi_list}
          >
            {!singleCoach && (
              <CoachInfo coach={coach} singleCoach={singleCoach} />
            )}
            {singleCoach ? (
              <DaysPanel
                currentDay={viewDate}
                coach={coach}
                timeSlots={slotsGroupedByDate}
                loading={loading}
                submitForm={submitForm}
                pricingValue={pricingValue}
              />
            ) : (
              <DaysPanelForAllCoaches
                currentDay={viewDate}
                coach={coach}
                timeSlots={
                  timeSlots[!isPublicPage ? coach.id : coach.calendarUuid] || []
                }
                index={index}
                // A tap in this view books straight away — there is no confirm
                // bar under it — so it needs the real handler, not the `null`
                // it used to be given.
                submitForm={submitForm}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEmptyPricings = () => {
    return (
      <div className={styles.rightPanel}>
        <i
          className={`ico ico-calendar ${styles.iconStyle}`}
          aria-label="date"
        />
        <p>
          {t("{{fullName}} is not set up to allow bookings by athletes.", {
            fullName: coaches[0].fullName,
          })}
        </p>
        <p>
          {t("Please contact {{fullName}} to book a session.", {
            fullName: coaches[0].fullName,
          })}
        </p>
      </div>
    );
  };

  const renderBookingLimitReached = () => {
    return (
      <div className={styles.booking_limit_reached}>
        <div className={styles.limit_icon}>
          <i className="ico ico-warning" />
        </div>
        <div className={styles.limit_message}>
          {t("Booking limit reached")}
        </div>
        <div className={styles.limit_details}>
          {t("You have reached the maximum number of future bookings for this option.")}
        </div>
        <div className={styles.limit_hint}>
          {t("Please wait for an existing booking to complete before booking again.")}
        </div>
      </div>
    );
  };


  return (
    <div className={styles.container}>
      {/* Just the day. Who it is with and which option are the cards above
          (BookingCards); this header used to repeat both. */}
      <div className={styles.header}>
        <div className={styles.info_container}>
          <div className={styles.date_container}>
            {new Intl.DateTimeFormat(i18n.language, {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(viewDate.toJSDate())}
          </div>
        </div>
      </div>
      {isBookingLimitReached
        ? renderBookingLimitReached()
        : hasPricings
          ? renderCoaches()
          : singleCoach === true
            ? renderEmptyPricings()
            : renderCoaches()}
    </div>
  );
};

export default DateAndTime;
